/**
 * PRP-WEB-2 W2.5: the selected room's group in the side panel. The main room, then its subrooms
 * in order with their numbers (the Xactimate subroom number: F1, PF2 address them by it). Drag a
 * row (or use its up/down buttons) to reorder; each subroom has its own ceiling height and
 * type (a two-story area is a tall ceiling). Grouping never changes walls.
 */
import { useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical, X } from 'lucide-react';
import { findRoom, setCeiling, setCeilingType, POLICY, type Plan } from '@sketch-core';
import { FeetInchesField } from '../FeetInchesField';
import { ceilingDraft } from '../menus/menuModel';
import { groupOf, removeFromGroup, reorderGroup, type GroupResult, type Groups } from './groups';

type Act = (p: Plan) => { ok: boolean; message?: string };

export interface SubroomListProps {
  plan: Plan;
  groups: Groups;
  rid: string;
  editable: (rid: string) => string | null;
  onAct: (a: Act) => string | null;
  onGroups: (fn: (g: Groups) => GroupResult) => string | null;
  onSelect: (rid: string) => void;
}

const iconBtn = 'grid h-7 w-7 place-items-center rounded-[var(--r-sm)] text-[var(--tx-mid)] hover:bg-[var(--bg-4)] hover:text-[var(--tx-hi)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)] disabled:opacity-30 [&_svg]:h-3.5 [&_svg]:w-3.5';

export function SubroomList({ plan, groups, rid, editable, onAct, onGroups, onSelect }: SubroomListProps) {
  const [dragging, setDragging] = useState<string | null>(null);
  const at = groupOf(groups, rid);
  if (!at) {
    return (
      <section data-testid="subroom-none" className="text-[12px] text-[var(--tx-faint)]">
        <h3 className="mb-1 text-[12px] font-[620] uppercase tracking-[.04em] text-[var(--tx-lo)]">Subrooms</h3>
        Not grouped. Right-click a room and choose Make subroom of… to join it to a main room.
      </section>
    );
  }
  const g = at.group;
  const main = findRoom(plan, g.main_room_id);
  const move = (sub: string, to: number) => onGroups((G) => reorderGroup(G, g.main_room_id, sub, to));

  return (
    <section data-testid="subroom-list">
      <h3 className="mb-2 text-[12px] font-[620] uppercase tracking-[.04em] text-[var(--tx-lo)]">
        Subrooms of {main?.name ?? 'the main room'}
      </h3>
      <p className="mb-2 text-[11.5px] text-[var(--tx-faint)]">The number is the subroom number in Xactimate (F1 is subroom 1). Drag to reorder.</p>
      <ol className="space-y-2">
        {g.subroom_ids.map((sid, i) => {
          const r = findRoom(plan, sid);
          if (!r) return null;
          const ro = editable(sid);
          return (
            <li key={sid} data-testid="subroom-row" data-room={sid}
              draggable onDragStart={(e) => { setDragging(sid); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', sid); }}
              onDragOver={(e) => { if (dragging) e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (dragging && dragging !== sid) move(dragging, i); setDragging(null); }}
              onDragEnd={() => setDragging(null)}
              className={`rounded-[var(--r-md)] border bg-[var(--bg-1)] p-2 ${sid === rid ? 'border-[var(--accent)]' : 'border-[var(--bd-subtle)]'} ${dragging === sid ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-1.5">
                <GripVertical className="h-4 w-4 cursor-grab text-[var(--tx-faint)]" aria-hidden />
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--accent)] text-[11px] font-[700] text-[var(--accent-tx)]"
                  data-testid="subroom-number">{i + 1}</span>
                <button type="button" onClick={() => onSelect(sid)}
                  className="min-w-0 flex-1 truncate rounded-[var(--r-sm)] text-left text-[12.5px] font-[600] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]">{r.name}</button>
                <button type="button" className={iconBtn} aria-label={`Move ${r.name} up`} disabled={i === 0} onClick={() => move(sid, i - 1)}><ArrowUp /></button>
                <button type="button" className={iconBtn} aria-label={`Move ${r.name} down`} disabled={i === g.subroom_ids.length - 1} onClick={() => move(sid, i + 1)}><ArrowDown /></button>
                <button type="button" className={iconBtn} aria-label={`Remove ${r.name} from the group`} title="Remove from group"
                  onClick={() => onGroups((G) => ({ ok: true, groups: removeFromGroup(G, sid) }))}><X /></button>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <FeetInchesField label="Ceiling height" valueFt={r.ceilingFt} disabled={!!ro}
                  onCommit={(ft) => onAct((p) => setCeiling(p, sid, ft)) ?? undefined} />
                <label className="text-[11.5px] text-[var(--tx-lo)]">
                  <span className="mb-1 block font-[560]">Ceiling type</span>
                  <select value={r.ceiling?.type ?? 'flat'} disabled={!!ro} aria-label={`Ceiling type of ${r.name}`}
                    onChange={(e) => onAct((p) => setCeilingType(p, sid, ceilingDraft(e.target.value, r)))}
                    className="h-[34px] w-full rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-inset)] px-2 text-[13px] text-[var(--tx-hi)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]">
                    {Object.entries(POLICY.ceiling_types).map(([k, v]) => <option key={k} value={k}>{(v as { label: string }).label}</option>)}
                  </select>
                </label>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export default SubroomList;
